import { createFileRoute, Link } from "@tanstack/react-router";
import { 
  TrendingUp, 
  TrendingDown, 
  DollarSign, 
  Calendar, 
  Clock, 
  ChevronRight,
  Package,
  ArrowUpRight,
  ArrowDownRight,
  Wallet
} from "lucide-react";
import { format, parseISO, endOfMonth } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { useMemo, useEffect, useState, useCallback } from "react";
import { useMonthSelector } from "@/hooks/useMonthSelector";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  Legend
} from "recharts";
import { supabase } from "@/lib/supabase";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import { useUserRole } from "@/hooks/useUserRole";


export const Route = createFileRoute("/")({
  component: DashboardPage,
});

function DashboardPage() {
  const [nomeUsuario, setNomeUsuario] = useState("");
  const [financeiro, setFinanceiro] = useState<any[]>([]);
  const [agendamentos, setAgendamentos] = useState<any[]>([]);
  const [estoqueBaixo, setEstoqueBaixo] = useState<any[]>([]);
  const [contasRecorrentes, setContasRecorrentes] = useState<any[]>([]);
  const [chartData, setChartData] = useState<{ mes: string; ganhos: number; despesas: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const { selectedDate, MonthSelector } = useMonthSelector();
  const { isTatuador, comissaoPercentual, effectiveStudioId, permissaoEstoque, loading: roleLoading } = useUserRole();

  const fetchData = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user?.id) {
        const { data: userData } = await supabase
          .from("usuarios")
          .select("nome")
          .eq("id", session.user.id)
          .single();

        const nome =
          userData?.nome ||
          session.user.user_metadata?.nome ||
          session.user.user_metadata?.full_name ||
          session.user.email?.split("@")[0] ||
          "";
        setNomeUsuario(nome);

        const start = format(selectedDate, "yyyy-MM-01");
        const end = format(endOfMonth(selectedDate), "yyyy-MM-dd");

        // Buscar dados financeiros do studio filtrados pelo mês
        // (convidado não acessa o financeiro inteiro do studio — a comissão
        // dele é calculada a partir dos próprios agendamentos, abaixo)
        const { data: finData } = isTatuador
          ? { data: [] as any[] }
          : await supabase
              .from("financeiro")
              .select("*")
              .eq("studio_id", effectiveStudioId)
              .in("tipo", ["entrada", "saida"])
              .gte("data", start)
              .lte("data", end);
        setFinanceiro(finData || []);

        // Buscar agendamentos filtrados pelo mês
        // Tatuador convidado: filtra por tatuador_id (os agendamentos dele
        // pertencem ao studio do dono). Dono: filtra por studio_id.
        const agendQuery = supabase
          .from("agendamentos")
          .select("*")
          .gte("data", start)
          .lte("data", end);
        const { data: agendData } = await (isTatuador
          ? agendQuery.eq("tatuador_id", session.user.id)
          : agendQuery.eq("studio_id", session.user.id));
        setAgendamentos(agendData || []);

        // Buscar estoque baixo: só quando o convidado tem acesso liberado ao estoque
        if (!isTatuador || permissaoEstoque === "todos") {
          const { data: estData } = await supabase
            .from("estoque")
            .select("id, nome, nivel_atual, tipo_controle, quantidade_atual, quantidade_minima")
            .eq("studio_id", effectiveStudioId);

          const itensBaixos = (estData || []).filter(item => {
            if (item.tipo_controle === "Por nível") {
              return item.nivel_atual === "Baixo" || item.nivel_atual === "Crítico";
            }
            return (item.quantidade_atual ?? 0) <= (item.quantidade_minima ?? 0);
          });
          setEstoqueBaixo(itensBaixos);
        } else {
          setEstoqueBaixo([]);
        }

        // Contas recorrentes são um assunto financeiro do dono — não aparecem pro convidado
        if (!isTatuador) {
          const { data: contasData } = await supabase
            .from("contas_recorrentes")
            .select("id, nome, dia_vencimento")
            .eq("studio_id", effectiveStudioId)
            .eq("ativo", true);
          setContasRecorrentes(contasData || []);
        } else {
          setContasRecorrentes([]);
        }

        // Buscar financeiro do ano inteiro para o gráfico (idem: não se aplica ao convidado)
        const anoAtual = new Date().getFullYear();
        const { data: finAnual } = isTatuador
          ? { data: [] as any[] }
          : await supabase
              .from("financeiro")
              .select("tipo, valor, data")
              .eq("studio_id", effectiveStudioId)
              .in("tipo", ["entrada", "saida"])
              .gte("data", `${anoAtual}-01-01`)
              .lte("data", `${anoAtual}-12-31`);

        const meses = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
        setChartData(meses.map((mes, idx) => {
          const monthStr = String(idx + 1).padStart(2, "0");
          const mes_data = (finAnual || []).filter(f => f.data.startsWith(`${anoAtual}-${monthStr}`));
          return {
            mes,
            ganhos: mes_data.filter(f => f.tipo === "entrada").reduce((acc, f) => acc + f.valor, 0),
            despesas: mes_data.filter(f => f.tipo === "saida").reduce((acc, f) => acc + f.valor, 0),
          };
        }));
      }
    } catch (error) {
      console.error("Erro ao carregar dados do dashboard:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, isTatuador, effectiveStudioId, permissaoEstoque]);

  useEffect(() => {
    if (!roleLoading) fetchData();
  }, [roleLoading, fetchData]);

  // Atualiza dashboard em tempo real quando houver mudanças nas tabelas principais
  useRealtimeSubscription("agendamentos", fetchData);
  useRealtimeSubscription("financeiro", fetchData);
  useRealtimeSubscription("estoque", fetchData);
  
  const greeting = useMemo(() => {
    const hour = new Date().getHours();
    if (hour < 12) return "Bom dia";
    if (hour < 18) return "Boa tarde";
    return "Boa noite";
  }, []);

  const currentMonthLabel = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"][new Date().getMonth()];

  const formatCurrency = (val: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(val);
  };

  const stats = useMemo(() => {
    // Faturamento = agendamentos de clientes não cancelados + entradas manuais no financeiro
    const faturamentoAgendamentos = agendamentos
      .filter(a => a.status !== "Cancelado" && a.tipo === "cliente")
      .reduce((acc, a) => acc + (parseFloat(a.valor_total) || 0), 0);
    const entradasManuais = financeiro
      .filter(f => f.tipo === "entrada" && !f.geradoAutomaticamente)
      .reduce((acc, f) => acc + f.valor, 0);
    const faturamento = faturamentoAgendamentos + entradasManuais;
    const despesas = financeiro.filter(f => f.tipo === "saida").reduce((acc, curr) => acc + curr.valor, 0);
    // Comissão só existe para o tatuador convidado (percentual pago ao studio)
    const comissao = isTatuador ? (faturamentoAgendamentos * comissaoPercentual) / 100 : 0;
    const lucro = faturamento - despesas - comissao;
    return { faturamento, despesas, comissao, lucro };
  }, [financeiro, agendamentos, isTatuador, comissaoPercentual]);

  const pagamentosPendentes = useMemo(() => {
    return agendamentos
      .filter(a => a.status === "Confirmado" || a.status === "Reagendado")
      .reduce((acc, curr) => acc + ((curr.valor_total || 0) - (curr.valor_sinal || 0)), 0);
  }, [agendamentos]);

  const sinaisPagos = useMemo(() => {
    return agendamentos
      .filter(a => a.status !== "Cancelado")
      .reduce((acc, curr) => acc + (curr.valor_sinal || 0), 0);
  }, [agendamentos]);

  const totalAReceber = useMemo(() => {
    return agendamentos
      .filter(a => a.status !== "Cancelado" && a.status !== "Concluído")
      .reduce((acc, curr) => acc + ((curr.valor_total || 0) - (curr.valor_sinal || 0)), 0);
  }, [agendamentos]);

  const contasVencendo = useMemo(() => {
    const diaHoje = new Date().getDate();
    const diaFim = diaHoje + 7;
    return contasRecorrentes.filter(c => c.dia_vencimento >= diaHoje && c.dia_vencimento <= diaFim);
  }, [contasRecorrentes]);

  const agendamentosDoMes = useMemo(() => {
    return agendamentos
      .sort((a, b) => {
        const dateCompare = a.data.localeCompare(b.data);
        if (dateCompare !== 0) return dateCompare;
        return (a.horario_inicio || "").localeCompare(b.horario_inicio || "");
      })
      .slice(0, 5);
  }, [agendamentos]);

  return (
    <div className="space-y-12 animate-in fade-in duration-700 pb-20">
      {/* Saudação */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-4xl md:text-5xl font-semibold text-foreground tracking-tighter">
            {greeting}{nomeUsuario ? `, ` : `!`}
            {nomeUsuario && <span className="text-primary">{nomeUsuario}</span>}
            {nomeUsuario && `!`}
          </h1>
          <p className="text-muted-foreground font-medium mt-2 capitalize">
            {format(new Date(), "EEEE, dd 'de' MMMM", { locale: ptBR })}
          </p>
        </div>
        <MonthSelector />
      </div>

      {/* LINHA 1 — Cards de resumo financeiro */}
      <div className={cn("grid grid-cols-1 gap-6", isTatuador ? "md:grid-cols-2 xl:grid-cols-4" : "md:grid-cols-3")}>
        <Card className="bg-card border-border hover:border-primary/20 transition-all duration-300 group shadow-sm">
          <CardContent className="p-8">
            <div className="flex justify-between items-start mb-6">
              <div className="size-12 rounded-2xl bg-emerald-50 flex items-center justify-center text-emerald-600 group-hover:scale-110 transition-transform">
                <ArrowUpRight size={28} />
              </div>
            </div>
            <p className="text-muted-foreground text-xs font-medium uppercase tracking-[0.2em] mb-2">Faturamento do Mês</p>
            <h3 className="text-3xl font-semibold text-emerald-600 tracking-tight">{formatCurrency(stats.faturamento)}</h3>
          </CardContent>
        </Card>

        <Card className="bg-card border-border hover:border-primary/20 transition-all duration-300 group shadow-sm">
          <CardContent className="p-8">
            <div className="flex justify-between items-start mb-6">
              <div className="size-12 rounded-2xl bg-red-50 flex items-center justify-center text-primary group-hover:scale-110 transition-transform">
                <ArrowDownRight size={28} />
              </div>
            </div>
            <p className="text-muted-foreground text-xs font-medium uppercase tracking-[0.2em] mb-2">Despesas do Mês</p>
            <h3 className="text-3xl font-semibold text-primary tracking-tight">{formatCurrency(stats.despesas)}</h3>
          </CardContent>
        </Card>

        {isTatuador && (
          <Card className="bg-card border-border hover:border-primary/20 transition-all duration-300 group shadow-sm">
            <CardContent className="p-8">
              <div className="flex justify-between items-start mb-6">
                <div className="size-12 rounded-2xl bg-amber-50 flex items-center justify-center text-amber-600 group-hover:scale-110 transition-transform">
                  <ArrowDownRight size={28} />
                </div>
                <Badge className="bg-amber-50 text-amber-600 border-none font-semibold">{comissaoPercentual}%</Badge>
              </div>
              <p className="text-muted-foreground text-xs font-medium uppercase tracking-[0.2em] mb-2">Comissão do Mês</p>
              <h3 className="text-3xl font-semibold text-amber-600 tracking-tight">{formatCurrency(stats.comissao)}</h3>
            </CardContent>
          </Card>
        )}

        <Card className="bg-card border-border hover:border-primary/20 transition-all duration-300 group shadow-sm">
          <CardContent className="p-8">
            <div className="flex justify-between items-start mb-6">
              <div className="size-12 rounded-2xl bg-muted/30 flex items-center justify-center text-foreground group-hover:scale-110 transition-transform">
                <DollarSign size={28} />
              </div>
            </div>
            <p className="text-muted-foreground text-xs font-medium uppercase tracking-[0.2em] mb-2">Lucro Líquido</p>
            <h3 className={cn(
              "text-3xl font-semibold tracking-tight",
              stats.lucro >= 0 ? "text-foreground" : "text-primary"
            )}>{formatCurrency(stats.lucro)}</h3>
          </CardContent>
        </Card>
      </div>

      {/* LINHA 2 — Cards de recebimentos */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="flex flex-col">
          <p className="text-sm font-semibold text-muted-foreground uppercase tracking-widest mb-3 ml-1">Sinais Pagos</p>
          <div className="bg-card border border-border rounded-2xl p-6 flex items-center justify-between shadow-sm">
            <h4 className="text-3xl font-bold text-emerald-600">{formatCurrency(sinaisPagos)}</h4>
            <div className="size-8 rounded-lg bg-emerald-50 flex items-center justify-center text-emerald-600">
              <TrendingUp size={18} />
            </div>
          </div>
        </div>
        <div className="flex flex-col">
          <p className="text-sm font-semibold text-muted-foreground uppercase tracking-widest mb-3 ml-1">Pagamentos Pendentes</p>
          <div className="bg-card border border-border rounded-2xl p-6 flex items-center justify-between shadow-sm">
            <h4 className="text-3xl font-bold text-amber-600">{formatCurrency(pagamentosPendentes)}</h4>
            <div className="size-8 rounded-lg bg-amber-50 flex items-center justify-center text-amber-600">
              <Clock size={18} />
            </div>
          </div>
        </div>
        <div className="flex flex-col">
          <p className="text-sm font-semibold text-muted-foreground uppercase tracking-widest mb-3 ml-1">Total a Receber (Mês)</p>
          <div className="bg-card border border-border rounded-2xl p-6 flex items-center justify-between shadow-sm">
            <h4 className="text-3xl font-bold text-foreground">{formatCurrency(totalAReceber)}</h4>
            <div className="size-8 rounded-lg bg-muted/30 flex items-center justify-center text-foreground">
              <Calendar size={18} />
            </div>
          </div>
        </div>
      </div>

      {/* LINHA 3 — Próximos Agendamentos */}
      <div className="space-y-6">
        <div className="flex items-center justify-between px-2">
          <h2 className="text-2xl font-semibold text-foreground flex items-center gap-3">
            Agendamentos do Mês
          </h2>
          <Link to="/agenda" className="text-xs font-medium text-primary uppercase tracking-widest hover:underline">Ver agenda completa</Link>
        </div>

        <div className="bg-card border border-border rounded-3xl overflow-hidden shadow-sm">
          {agendamentosDoMes.length > 0 ? (
            <div className="divide-y divide-border">
              {agendamentosDoMes.map(a => (
                <div key={a.id} className="p-6 flex items-center justify-between hover:bg-muted/30 transition-colors group">
                  <div className="flex items-center gap-6">
                    <div className="bg-background border border-border rounded-xl px-4 py-3 text-center min-w-[70px]">
                      <p className="text-[10px] font-medium text-muted-foreground uppercase">{format(parseISO(a.data), "MMM", { locale: ptBR })}</p>
                      <p className="text-lg font-semibold text-foreground">{format(parseISO(a.data), "dd")}</p>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-semibold text-primary">{a.horario_inicio?.slice(0, 5)}</span>
                        <span className="text-border">•</span>
                        <p className="text-lg font-semibold text-foreground group-hover:text-primary transition-colors">{a.cliente_nome}</p>
                      </div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-widest">{a.estilo}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-8">
                    <div className="text-right hidden sm:block">
                      <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-widest">Valor</p>
                      <p className="text-sm font-semibold text-foreground">{formatCurrency(a.valor_total)}</p>
                    </div>
                    <Badge className={cn(
                      "text-[10px] font-medium uppercase px-3 py-1",
                      a.status === "Confirmado" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"
                    )}>{a.status}</Badge>
                    <ChevronRight className="text-border group-hover:text-foreground transition-colors" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-16 text-center">
              <p className="text-muted-foreground font-semibold italic">Nenhum agendamento futuro</p>
            </div>
          )}
        </div>
      </div>

      {/* LINHA 4 — Alertas Importantes e Gráfico */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        {/* Alertas Importantes */}
        <div className="lg:col-span-4 space-y-6">
          <h3 className="text-xl font-medium text-foreground px-2 uppercase tracking-tighter">Alertas Importantes</h3>
          <div className="space-y-4">
            {/* Contas Vencendo — só aparece quando há contas na semana */}
            {contasVencendo.length > 0 && (
              <div className="bg-card border border-border rounded-2xl p-6 border-l-4 border-l-primary hover:scale-[1.02] transition-transform cursor-pointer shadow-sm">
                <div className="flex items-center gap-4 mb-5">
                  <div className="size-12 rounded-2xl bg-red-50 flex items-center justify-center text-primary">
                    <Wallet size={24} />
                  </div>
                  <div>
                    <p className="text-base font-semibold text-foreground leading-none">Vencendo esta semana</p>
                    <p className="text-[10px] text-muted-foreground uppercase font-medium mt-1.5 tracking-wider">Compromissos financeiros</p>
                  </div>
                </div>
                <div className="space-y-3">
                  {contasVencendo.map(c => (
                    <div key={c.id} className="flex items-center justify-between bg-background p-3 rounded-xl border border-border/50">
                      <span className="text-xs font-semibold text-muted-foreground">{c.nome}</span>
                      <Badge variant="outline" className="text-[10px] font-semibold border-primary text-primary">Dia {c.dia_vencimento}</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Estoque Baixo — só aparece quando há itens em alerta */}
            {estoqueBaixo.length > 0 && (
              <div className="bg-card border border-border rounded-2xl p-6 border-l-4 border-l-amber-500 hover:scale-[1.02] transition-transform cursor-pointer shadow-sm">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex items-center gap-4">
                    <div className="size-12 rounded-2xl bg-amber-50 flex items-center justify-center text-amber-600">
                      <Package size={24} />
                    </div>
                    <div>
                      <p className="text-base font-semibold text-foreground leading-none">Estoque Baixo</p>
                      <p className="text-[10px] text-muted-foreground uppercase font-medium mt-1.5 tracking-wider">
                        {estoqueBaixo.length} {estoqueBaixo.length === 1 ? "produto" : "produtos"} precisam de reposição
                      </p>
                    </div>
                  </div>
                  <Link to="/estoque" className="size-8 rounded-full hover:bg-muted flex items-center justify-center transition-colors">
                    <ChevronRight size={18} className="text-amber-600" />
                  </Link>
                </div>
                <div className="space-y-2">
                  {estoqueBaixo.map(i => {
                    const nivel = i.tipo_controle === "Por unidade"
                      ? ((i.quantidade_atual ?? 0) <= 0 ? "Crítico" : "Baixo")
                      : i.nivel_atual;
                    return (
                      <div key={i.id} className="flex items-center justify-between bg-background p-3 rounded-xl border border-border/50">
                        <span className="text-xs font-medium text-muted-foreground">{i.nome}</span>
                        <Badge variant="outline" className={cn(
                          "text-[9px] font-medium uppercase",
                          nivel === "Crítico" ? "border-primary text-primary" : "border-amber-500 text-amber-500"
                        )}>{nivel}</Badge>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Gráfico de Desempenho */}
        <div className="lg:col-span-8 space-y-6">
          <h3 className="text-xl font-medium text-foreground px-2 uppercase tracking-tighter">Desempenho do Ano</h3>
          <Card className="bg-card border-border p-8 h-[440px] flex flex-col shadow-sm">
            <div className="flex-1 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke="#E5E7EB" strokeDasharray="3 3" />
                  <XAxis 
                    dataKey="mes" 
                    axisLine={false}
                    tickLine={false}
                    tick={({ x, y, payload }) => (
                      <g transform={`translate(${x},${y})`}>
                        <text
                          x={0}
                          y={0}
                          dy={16}
                          textAnchor="middle"
                          fill={payload.value === currentMonthLabel ? "#DC2626" : "#6B7280"}
                          className="text-[10px] font-medium uppercase tracking-tighter"
                        >
                          {payload.value}
                        </text>
                      </g>
                    )}
                  />
                  <YAxis 
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: '#6B7280', fontSize: 10, fontWeight: 900 }}
                  />
                  <Tooltip 
                    contentStyle={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E7EB', borderRadius: '12px', fontSize: '12px' }}
                    itemStyle={{ fontWeight: 800 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="ganhos" 
                    stroke="#10B981" 
                    strokeWidth={3}
                    dot={{ fill: '#10B981', strokeWidth: 2, r: 4, stroke: '#FFFFFF' }}
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="despesas" 
                    stroke="#DC2626" 
                    strokeWidth={3}
                    dot={{ fill: '#DC2626', strokeWidth: 2, r: 4, stroke: '#FFFFFF' }}
                    activeDot={{ r: 6, strokeWidth: 0 }}
                  />
                  <Legend 
                    verticalAlign="bottom" 
                    height={36}
                    content={({ payload }) => (
                      <div className="flex items-center justify-center gap-8 mt-6">
                        {payload?.map((entry: any, index: number) => (
                          <div key={index} className="flex items-center gap-2">
                            <div className="size-2 rounded-full" style={{ backgroundColor: entry.color }}></div>
                            <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-widest">
                              {entry.value === "ganhos" ? "Ganhos" : "Despesas"}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}
